import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GitCompareArrows, RefreshCw, Activity } from "lucide-react";

import { Button } from "@/components/ui/button";
import { ContentContainer, PageLayout, PageTitle } from "components/layout";
import { ErrorBoundary } from "@/components/error-boundary";
import { isValidCampaignType } from "@/utils/campaign-types";
import { CampaignType as CampaignTypeEnum } from "@/@types/campaign";
import type { CampaignType } from "src/@types/campaign-propagation";

import {
  CampaignCombobox,
  type SelectedCampaign,
} from "./components/campaign-combobox";
import { ComparisonBanners } from "./components/comparison-banners";
import { ComparisonCampaignTypeFilter } from "./components/comparison-campaign-type-filter";
import { ComparisonSummaryCards } from "./components/comparison-summary-cards";
import { ComparisonTableToolbar } from "./components/comparison-table-toolbar";
import { RetaguardaCard } from "./components/retaguarda-card";
import { StoreComparisonDialog } from "./components/store-comparison-dialog";
import { StoreRawComparisonDialog } from "./components/store-raw-comparison-dialog";
import { DataTable } from "./components/data-table";
import { buildColumns, type ComparisonRow } from "./components/columns";
import { useFilterActions, useFilterState } from "./filter";
import { useCampaignComparison } from "./hooks/use-campaign-comparison";

const DEFAULT_COMPARABLE_TYPES = [
  CampaignTypeEnum.PRO013,
  CampaignTypeEnum.PRO014,
  CampaignTypeEnum.PRO017,
];

export function CampaignComparison() {
  const { type, code } = useParams<{ type?: string; code?: string }>();
  const navigate = useNavigate();
  const filters = useFilterState();
  const { updateFilters } = useFilterActions();

  const [selectedStore, setSelectedStore] = useState<ComparisonRow | null>(null);
  const [rawOpen, setRawOpen] = useState(false);
  const [campaignTypes, setCampaignTypes] = useState<CampaignTypeEnum[]>(
    DEFAULT_COMPARABLE_TYPES
  );

  const validType =
    type && isValidCampaignType(type) ? (type as CampaignType) : undefined;
  const numericCode = code ? parseInt(code, 10) : undefined;
  const showWorkspace = !!validType && !!numericCode;

  const {
    result,
    progress,
    retaguarda,
    summary,
    rows,
    filteredRows,
    pagination,
    hasItemComparison,
    isProcessing,
    error,
    refresh,
  } = useCampaignComparison({ validType, numericCode, filters });

  const columns = useMemo(
    () =>
      buildColumns({ onDetail: setSelectedStore, hasItemComparison }),
    [hasItemComparison]
  );

  const campaignName = retaguarda?.description?.trim();
  const triggerLabel = showWorkspace
    ? campaignName
      ? `${campaignName} - [${validType} #${numericCode}]`
      : `[${validType} #${numericCode}]`
    : undefined;

  const goToSync = (storeCode?: number) =>
    navigate(
      `/planejamento-comercial/auditoria-propagacao/${validType}/${numericCode}${storeCode ? `?storeCode=${storeCode}` : ""}`,
      { state: { fromComparison: true } }
    );

  return (
    <ErrorBoundary>
      <PageLayout>
        <PageTitle
          title="Comparativo Retaguarda vs Loja"
          description="Compare o que está cadastrado na retaguarda com o que está em cada loja participante da campanha"
        />
        <ContentContainer>
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <ComparisonCampaignTypeFilter
                selectedTypes={campaignTypes}
                onTypesChange={setCampaignTypes}
              />
              <CampaignCombobox
                campaignTypes={campaignTypes}
                triggerLabel={triggerLabel}
                onSelect={(c: SelectedCampaign) =>
                  navigate(
                    `/planejamento-comercial/comparativo-retaguarda/${c.type}/${c.code}`
                  )
                }
              />
              {showWorkspace && (
                <>
                  <Button
                    variant="outline"
                    onClick={refresh}
                    disabled={isProcessing}
                  >
                    <RefreshCw
                      className={`mr-2 h-4 w-4 ${isProcessing ? "animate-spin" : ""}`}
                    />
                    Atualizar
                  </Button>
                  <Button variant="outline" onClick={() => goToSync()}>
                    <Activity className="mr-2 h-4 w-4" />
                    Ir para sincronização
                  </Button>
                </>
              )}
            </div>

            {!showWorkspace ? (
              <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed p-12 text-center">
                <GitCompareArrows className="h-8 w-8 text-muted-foreground" />
                <div className="font-medium">
                  Busque e selecione uma campanha pelo nome
                </div>
                <div className="max-w-md text-sm text-muted-foreground">
                  O sistema consulta o banco de cada loja participante e compara
                  com os dados da retaguarda — uma loja por vez.
                </div>
              </div>
            ) : (
              <>
                <ComparisonBanners error={error} result={result} />

                {summary && (
                  <ComparisonSummaryCards
                    summary={summary}
                    progress={
                      isProcessing && progress
                        ? { processed: progress.processed, total: progress.total }
                        : null
                    }
                  />
                )}
                {retaguarda && <RetaguardaCard retaguarda={retaguarda} />}

                <ComparisonTableToolbar
                  search={filters.search}
                  onSearchChange={(v) =>
                    updateFilters({ search: v }, { replace: true })
                  }
                  status={filters.status}
                  onStatusChange={(v) => updateFilters({ status: v })}
                  filteredCount={filteredRows.length}
                  totalCount={rows.length}
                />
                <DataTable
                  columns={columns}
                  data={filteredRows}
                  pagination={pagination}
                  isLoading={isProcessing && rows.length === 0}
                />

                {result && (
                  <div className="text-xs text-muted-foreground">
                    Comparação desta página executada em{" "}
                    {(result.summary.durationMs / 1000).toFixed(1)}s — conexão
                    serial nos bancos das lojas.
                  </div>
                )}
              </>
            )}
          </div>
        </ContentContainer>
      </PageLayout>

      <StoreComparisonDialog
        open={!!selectedStore && !rawOpen}
        onOpenChange={(open) => !open && setSelectedStore(null)}
        store={selectedStore}
        retaguarda={retaguarda}
        onShowRaw={() => setRawOpen(true)}
        onGoToSync={goToSync}
      />

      <StoreRawComparisonDialog
        open={rawOpen}
        onOpenChange={setRawOpen}
        store={selectedStore}
      />
    </ErrorBoundary>
  );
}
